import { useSimpleCampaigns } from "@/api/campaign.api";
import { useAuth } from "@/auth";
import { useTrialPeriod } from "@/hooks/use-trial-period";
import { trackEvent } from "@/services/amplitude.service";
import { newCampaignStateAtom } from "@/services/new-campaign.service";
import { useNavigate } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { RESET } from "jotai/utils";
import { useMemo, useState } from "react";
import { DialogTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Button } from "../generic/Button";
import { Heading } from "../generic/Heading";
import { Modal } from "../generic/Modal";
import { TrialCTAModal } from "../trial/TrialCTAModal";

export function NewCampaignButton(props: { className?: string }) {
  const navigate = useNavigate();
  const [newCampaignState, setNewCampaignState] = useAtom(newCampaignStateAtom);

  const { user } = useAuth();
  const trialPeriod = useTrialPeriod();
  const simpleCampaigns = useSimpleCampaigns();

  const hasOngoingCampaignCreation = useMemo(
    () => !!newCampaignState.draftId,
    [newCampaignState.draftId],
  );

  const [showDraftModal, setShowDraftModal] = useState(false);

  const trialCTAProps = useMemo(() => {
    if (trialPeriod) {
      if (trialPeriod.isExpired)
        return {
          title:
            "Din prøveperiode er udløbet. Kontakt os for at få adgang til Lasso Sales uden begrænsninger.",
        };
      if ((simpleCampaigns?.length ?? 0) >= 3)
        return { title: "Du kan kun lave 3 kampagner i din prøveperiode" };
    }
    return null;
  }, [trialPeriod, simpleCampaigns]);

  return (
    <DialogTrigger>
      <Button
        size="lg"
        className={twMerge("w-fit", props.className)}
        animate
        {...(trialCTAProps
          ? {
              onClick: () => {},
            }
          : hasOngoingCampaignCreation
            ? {
                onClick: () => {
                  setShowDraftModal(true);
                },
              }
            : {
                link: { to: "/campaigns/setup" },
              })}
      >
        Ny kampagne
      </Button>

      <Modal
        isOpen={showDraftModal}
        padding="md"
        onOpenChange={setShowDraftModal}
      >
        <div className="flex flex-col items-center justify-center gap-8">
          <Heading className="text-md font-medium">
            Fortsæt kampagneopsætning?
          </Heading>

          <p className="text-theme-on-background-variant text-center text-sm">
            Du har allerede en kampagneopsætning i gang. Du kan fortsætte denne,
            eller starte en ny.
          </p>

          <div className="flex gap-6">
            <Button
              animate
              intent="secondary"
              onPress={() => {
                trackEvent("Campaign creation discarded", user.id, {
                  draftId: newCampaignState.draftId,
                });
                setNewCampaignState(RESET);
                setShowDraftModal(false);
                navigate({
                  to: "/campaigns/setup",
                });
              }}
            >
              Ny kampagne
            </Button>

            <Button
              animate
              onClick={() => {
                setShowDraftModal(false);
                navigate({
                  to: "/campaigns/setup",
                });
              }}
            >
              Fortsæt
            </Button>
          </div>
        </div>
      </Modal>

      {trialCTAProps && <TrialCTAModal {...trialCTAProps} />}
    </DialogTrigger>
  );
}
